// src/components/Login.tsx

import { Button, Form, Input, Modal, message } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { loginSuccess } from "../../../store/action/authAction";
import { API_ENDPOINTS, apiFetch } from "../../../services/api";
import EditorialLogin from "./EditorialLogin";

/* =========================================================
   TYPES
========================================================= */

interface LoginFormValues {
  emailId: string;
  password: string;
}

interface ForgotPasswordValues {
  emailId?: string;
  otp?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/* =========================================================
   ICONS
========================================================= */

const PortalIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 7.5L12 3L20 7.5V16.5L12 21L4 16.5V7.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />

    <path
      d="M8 10L12 7.8L16 10V14L12 16.2L8 14V10Z"
      fill="currentColor"
    />
  </svg>
);

const MailIcon = () => (
  <svg
    className="h-[18px] w-[18px]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8V8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="h-[18px] w-[18px]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2h-1V7a5 5 0 00-10 0v4H6a2 2 0 00-2 2v6a2 2 0 002 2zm3-10V7a3 3 0 016 0v4H9z"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg
    className="h-[18px] w-[18px]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const BoltIcon = () => (
  <svg
    className="h-[18px] w-[18px]"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

/* =========================================================
   COMPONENT
========================================================= */

const LoginForm = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  /* =======================================================
     FORGOT PASSWORD STATE
  ======================================================= */

  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);

  const [forgotPasswordStep, setForgotPasswordStep] =
    useState<1 | 2>(1);

  const [modalLoading, setModalLoading] = useState(false);

  const [resetEmail, setResetEmail] = useState("");

  const [forgotPasswordForm] = Form.useForm();

  /* =======================================================
     LOGIN
  ======================================================= */

  const onFinish = async (values: LoginFormValues) => {
    setLoading(true);

    try {
      const response = await apiFetch(API_ENDPOINTS.login, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          emailId: values.emailId,
          password: values.password,
        }),
      });

      const result = await response.json();

      console.log("Login response:", result);

      if (response.ok && result.status == 200) {
        const data = {
          name: result.name,
          emailId: result.emailId,
          mobile: result.mobile,
          role: result.role,
          userId: result.userId,
          accessToken: result.accessToken,
          loginTime: new Date().getTime(),
          allowedMenuKeys: result?.allowedMenuKeys || [],
        };

        localStorage.setItem(
          "userDetails",
          JSON.stringify(data)
        );

        message.success("Login Successful!");

        dispatch(loginSuccess());

        const requestedRoute = (
          location.state as { from?: string } | null
        )?.from;

        navigate(
          requestedRoute?.startsWith("/admin/")
            ? requestedRoute
            : "/admin/dashboard",
          {
            replace: true,
          }
        );
      } else {
        message.error(
          result?.message || "Invalid credentials!"
        );
      }
    } catch (error) {
      console.error("Login error:", error);

      message.error(
        "Something went wrong! Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     OPEN FORGOT PASSWORD
  ======================================================= */

  const openForgotPasswordModal = () => {
    setForgotPasswordStep(1);

    setResetEmail("");

    forgotPasswordForm.resetFields();

    setIsForgotPasswordModalOpen(true);
  };

  /* =======================================================
     SEND OTP
  ======================================================= */

  const handleSendOtp = async (
    values: ForgotPasswordValues
  ) => {
    if (!values.emailId) return;

    setModalLoading(true);

    try {
      const response = await apiFetch(
        API_ENDPOINTS.forgotPassword,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            emailId: values.emailId,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status == 200) {
        message.success("OTP sent to your email!");

        setResetEmail(values.emailId);

        setForgotPasswordStep(2);

        forgotPasswordForm.resetFields();
      } else {
        message.error(
          result?.message ||
            "Failed to send OTP. Check email."
        );
      }
    } catch (error) {
      console.error(error);

      message.error(
        "Network error. Please try again."
      );
    } finally {
      setModalLoading(false);
    }
  };

  /* =======================================================
     RESET PASSWORD
  ======================================================= */

  const handleResetPassword = async (
    values: ForgotPasswordValues
  ) => {
    setModalLoading(true);

    try {
      const response = await apiFetch(
        API_ENDPOINTS.forgotPassword,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            emailId: resetEmail,
            otp: values.otp,
            newPassword: values.newPassword,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.status == 200) {
        message.success(
          "Password reset successfully! Please login."
        );

        setIsForgotPasswordModalOpen(false);

        setForgotPasswordStep(1);

        forgotPasswordForm.resetFields();
      } else {
        message.error(
          result?.message ||
            "Failed to reset password. Invalid OTP."
        );
      }
    } catch (error) {
      console.error(error);

      message.error(
        "Network error. Please try again."
      );
    } finally {
      setModalLoading(false);
    }
  };

  /* =======================================================
     TOKEN EXPIRY
  ======================================================= */

  const checkTokenExpiry = () => {
    const userData =
      localStorage.getItem("userDetails");

    if (!userData) return;

    try {
      const parsedData = JSON.parse(userData);

      const loginTime = parsedData.loginTime;

      const currentTime =
        new Date().getTime();

      const twentyFourHours =
        24 * 60 * 60 * 1000;

      if (
        currentTime - loginTime >
        twentyFourHours
      ) {
        localStorage.removeItem(
          "userDetails"
        );

        window.location.href = "/";
      }
    } catch (error) {
      console.error(
        "Invalid user data:",
        error
      );

      localStorage.removeItem(
        "userDetails"
      );
    }
  };

  useEffect(() => {
    checkTokenExpiry();
  }, []);

  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="fixed inset-0 h-[100dvh] w-full overflow-hidden bg-slate-100">
      <EditorialLogin loading={loading} onSubmit={onFinish} onForgotPassword={openForgotPasswordModal} />
      {/* =================================================
          BACKGROUND
      ================================================= */}

      <div className="hidden pointer-events-none absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-blue-200/40 blur-[100px]" />

      <div className="hidden pointer-events-none absolute -bottom-40 -right-32 h-[500px] w-[500px] rounded-full bg-indigo-200/40 blur-[120px]" />

      {/* =================================================
          PAGE CONTAINER
      ================================================= */}

      <div
        className="
          hidden relative
          flex h-full min-h-0
          w-full
          items-center
          justify-center
          p-3
          sm:p-5
          lg:p-8
        "
      >
        {/* =================================================
            MAIN CARD
        ================================================= */}

        <div
          className="
            grid
            w-full
            max-w-[1180px]
            overflow-hidden
            rounded-[28px]
            border
            border-slate-200/70
            bg-white
            shadow-[0_30px_80px_-30px_rgba(15,23,42,0.28)]
            h-full
            min-h-0
            max-h-[680px]
            lg:grid-cols-2
          "
        >
          {/* =============================================
              LEFT PANEL
          ============================================= */}

          <div
            className="
              relative
              hidden
              overflow-hidden
              bg-slate-950
              lg:flex
            "
          >
            {/* Background Effects */}

            <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />

            <div className="pointer-events-none absolute -bottom-28 -left-20 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />

            <div className="pointer-events-none absolute right-12 top-24 h-32 w-32 rounded-full border border-white/[0.06]" />

            <div className="pointer-events-none absolute right-20 top-32 h-16 w-16 rounded-full border border-white/[0.06]" />

            {/* Left Alignment Container */}

            <div
              className="
                relative
                z-10
                flex
                min-h-full
                w-full
                flex-col
                px-10
                py-10
                xl:px-14
                xl:py-12
              "
            >
              {/* =========================================
                  LOGO
              ========================================= */}

              <div className="flex items-center gap-3">
                <div
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-xl
                    bg-gradient-to-br
                    from-blue-500
                    to-indigo-600
                    text-white
                    shadow-lg
                    shadow-blue-500/20
                  "
                >
                  <PortalIcon />
                </div>

                <div className="text-left">
                  <h2
                    className="
                      text-[16px]
                      font-semibold
                      leading-5
                      text-white
                    "
                  >
                    Admin Portal
                  </h2>

                  <p
                    className="
                      mt-0.5
                      text-xs
                      leading-4
                      text-slate-400
                    "
                  >
                    Management Dashboard
                  </p>
                </div>
              </div>

              {/* =========================================
                  CENTER CONTENT
              ========================================= */}

              <div
                className="
                  my-auto
                  w-full
                  max-w-[470px]
                  py-10
                  text-left
                "
              >
                {/* Badge */}

                <div
                  className="
                    mb-5
                    inline-flex
                    items-center
                    gap-2
                    rounded-full
                    border
                    border-white/10
                    bg-white/[0.06]
                    px-3.5
                    py-2
                    text-xs
                    font-medium
                    text-blue-200
                    backdrop-blur-sm
                  "
                >
                  <span
                    className="
                      h-2
                      w-2
                      shrink-0
                      rounded-full
                      bg-emerald-400
                    "
                  />

                  Secure administration portal
                </div>

                {/* Main Heading */}

                <h1
                  className="
                    max-w-[460px]
                    text-left
                    text-[38px]
                    font-bold
                    leading-[1.15]
                    tracking-[-0.035em]
                    text-white
                    xl:text-[46px]
                  "
                >
                  Manage everything

                  <span
                    className="
                      mt-1
                      block
                      bg-gradient-to-r
                      from-blue-400
                      via-cyan-300
                      to-indigo-400
                      bg-clip-text
                      text-transparent
                    "
                  >
                    from one place.
                  </span>
                </h1>

                {/* Description */}

                <p
                  className="
                    mt-5
                    max-w-[430px]
                    text-left
                    text-[14px]
                    leading-6
                    text-slate-400
                  "
                >
                  Access your centralized dashboard to
                  manage users, content, analytics and
                  business operations securely.
                </p>

                {/* =========================================
                    FEATURE CARDS
                ========================================= */}

                <div
                  className="
                    mt-8
                    grid
                    w-full
                    max-w-[440px]
                    grid-cols-2
                    gap-3
                  "
                >
                  {/* Secure Access */}

                  <div
                    className="
                      rounded-2xl
                      border
                      border-white/[0.08]
                      bg-white/[0.05]
                      p-4
                      text-left
                      backdrop-blur-sm
                      transition
                      duration-300
                      hover:bg-white/[0.08]
                    "
                  >
                    <div
                      className="
                        mb-3
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-lg
                        bg-blue-500/15
                        text-blue-300
                      "
                    >
                      <ShieldIcon />
                    </div>

                    <h3
                      className="
                        text-sm
                        font-semibold
                        leading-5
                        text-white
                      "
                    >
                      Secure Access
                    </h3>

                    <p
                      className="
                        mt-1
                        text-xs
                        leading-5
                        text-slate-400
                      "
                    >
                      Protected authentication
                    </p>
                  </div>

                  {/* Fast */}

                  <div
                    className="
                      rounded-2xl
                      border
                      border-white/[0.08]
                      bg-white/[0.05]
                      p-4
                      text-left
                      backdrop-blur-sm
                      transition
                      duration-300
                      hover:bg-white/[0.08]
                    "
                  >
                    <div
                      className="
                        mb-3
                        flex
                        h-9
                        w-9
                        items-center
                        justify-center
                        rounded-lg
                        bg-indigo-500/15
                        text-indigo-300
                      "
                    >
                      <BoltIcon />
                    </div>

                    <h3
                      className="
                        text-sm
                        font-semibold
                        leading-5
                        text-white
                      "
                    >
                      Fast & Reliable
                    </h3>

                    <p
                      className="
                        mt-1
                        text-xs
                        leading-5
                        text-slate-400
                      "
                    >
                      Optimized experience
                    </p>
                  </div>
                </div>
              </div>

              {/* =========================================
                  FOOTER
              ========================================= */}

              <div
                className="
                  flex
                  items-center
                  justify-between
                  gap-4
                  border-t
                  border-white/[0.08]
                  pt-5
                  text-[11px]
                  text-slate-500
                "
              >
                <span>
                  © {new Date().getFullYear()} Admin Portal
                </span>

                <div className="flex items-center gap-2">
                  <span
                    className="
                      h-1.5
                      w-1.5
                      shrink-0
                      rounded-full
                      bg-emerald-400
                    "
                  />

                  <span>
                    All systems operational
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* =============================================
              RIGHT PANEL
          ============================================= */}

          <div
            className="
              login-form-scroll
              flex
              h-full
              min-h-0
              items-center
              justify-center
              overflow-y-auto
              bg-white
              px-5
              py-6
              sm:px-8
              sm:py-8
              md:px-12
              lg:min-h-0
              lg:px-10
              lg:py-10
              xl:px-16
            "
          >
            {/* IMPORTANT:
                Everything uses the same 420px width.
            */}

            <div
              className="
                w-full
                max-w-[420px]
                text-left
              "
            >
              {/* =========================================
                  MOBILE LOGO
              ========================================= */}

              <div
                className="
                  mb-10
                  flex
                  items-center
                  gap-3
                  lg:hidden
                "
              >
                <div
                  className="
                    flex
                    h-11
                    w-11
                    shrink-0
                    items-center
                    justify-center
                    rounded-xl
                    bg-gradient-to-br
                    from-blue-600
                    to-indigo-600
                    text-white
                    shadow-lg
                    shadow-blue-500/20
                  "
                >
                  <PortalIcon />
                </div>

                <div className="text-left">
                  <h2
                    className="
                      text-base
                      font-bold
                      leading-5
                      text-slate-900
                    "
                  >
                    Admin Portal
                  </h2>

                  <p
                    className="
                      mt-0.5
                      text-xs
                      leading-4
                      text-slate-400
                    "
                  >
                    Management Dashboard
                  </p>
                </div>
              </div>

              {/* =========================================
                  LOGIN HEADING
              ========================================= */}

              <div
                className="
                  mb-8
                  w-full
                  text-left
                "
              >
                <div
                  className="
                    mb-3
                    flex
                    items-center
                    gap-2
                  "
                >
                  <span
                    className="
                      h-[3px]
                      w-6
                      rounded-full
                      bg-blue-600
                    "
                  />

                  <span
                    className="
                      text-[11px]
                      font-bold
                      uppercase
                      tracking-[0.16em]
                      text-blue-600
                    "
                  >
                    Welcome Back
                  </span>
                </div>

                <h1
                  className="
                    text-left
                    text-[28px]
                    font-bold
                    leading-[1.2]
                    tracking-[-0.025em]
                    text-slate-900
                    sm:text-[32px]
                  "
                >
                  Sign in to your account
                </h1>

                <p
                  className="
                    mt-3
                    w-full
                    text-left
                    text-sm
                    leading-6
                    text-slate-500
                  "
                >
                  Enter your account credentials to
                  access your dashboard.
                </p>
              </div>

              {/* =========================================
                  LOGIN FORM
              ========================================= */}

              <Form
                name="basic"
                onFinish={onFinish}
                layout="vertical"
                requiredMark={false}
                className="w-full"
              >
                {/* EMAIL */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      Email Address
                    </span>
                  }
                  name="emailId"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message:
                        "Please enter a valid email address!",
                    },
                  ]}
                  className="!mb-5 w-full"
                >
                  <Input
                    size="large"
                    placeholder="Enter your email address"
                    prefix={
                      <span
                        className="
                          mr-2
                          flex
                          items-center
                          text-slate-400
                        "
                      >
                        <MailIcon />
                      </span>
                    }
                    className="
                      !h-[50px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !shadow-none
                      transition
                      hover:!border-blue-400
                      focus:!border-blue-500
                    "
                  />
                </Form.Item>

                {/* PASSWORD */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      Password
                    </span>
                  }
                  name="password"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter your password!",
                    },
                  ]}
                  className="!mb-0 w-full"
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter your password"
                    prefix={
                      <span
                        className="
                          mr-2
                          flex
                          items-center
                          text-slate-400
                        "
                      >
                        <LockIcon />
                      </span>
                    }
                    className="
                      !h-[50px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !shadow-none
                      transition
                      hover:!border-blue-400
                    "
                  />
                </Form.Item>

                {/* FORGOT PASSWORD */}

                <div
                  className="
                    flex
                    w-full
                    justify-end
                    py-3
                  "
                >
                  <button
                    type="button"
                    onClick={
                      openForgotPasswordModal
                    }
                    className="
                      p-0
                      text-right
                      text-[13px]
                      font-semibold
                      leading-5
                      text-blue-600
                      transition-colors
                      duration-200
                      hover:text-blue-700
                    "
                  >
                    Forgot password?
                  </button>
                </div>

                {/* SIGN IN */}

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={loading}
                  className="
                    !mt-1
                    !flex
                    !h-[50px]
                    !w-full
                    !items-center
                    !justify-center
                    !rounded-xl
                    !border-0
                    !bg-gradient-to-r
                    !from-blue-600
                    !to-indigo-600
                    !text-sm
                    !font-semibold
                    !shadow-lg
                    !shadow-blue-500/20
                    transition-all
                    duration-300
                    hover:!-translate-y-[1px]
                    hover:!from-blue-700
                    hover:!to-indigo-700
                    hover:!shadow-xl
                    hover:!shadow-blue-500/25
                  "
                >
                  {loading
                    ? "Signing in..."
                    : "Sign In"}
                </Button>
              </Form>

              {/* =========================================
                  SECURITY BOX
              ========================================= */}

              <div
                className="
                  mt-6
                  flex
                  w-full
                  items-start
                  gap-3
                  rounded-xl
                  border
                  border-slate-100
                  bg-slate-50
                  p-3.5
                  text-left
                "
              >
                <div
                  className="
                    flex
                    h-8
                    w-8
                    shrink-0
                    items-center
                    justify-center
                    rounded-lg
                    bg-emerald-50
                    text-emerald-600
                  "
                >
                  <ShieldIcon />
                </div>

                <div
                  className="
                    min-w-0
                    text-left
                  "
                >
                  <p
                    className="
                      text-xs
                      font-semibold
                      leading-5
                      text-slate-700
                    "
                  >
                    Secure Authentication
                  </p>

                  <p
                    className="
                      mt-0.5
                      text-[11px]
                      leading-5
                      text-slate-400
                    "
                  >
                    Your credentials are securely
                    processed and protected.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================
          FORGOT PASSWORD MODAL
      ================================================= */}

      <Modal
        open={isForgotPasswordModalOpen}
        onCancel={() => {
          setIsForgotPasswordModalOpen(
            false
          );

          setForgotPasswordStep(1);

          forgotPasswordForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        centered
        width={460}
        title={null}
        className="forgot-password-modal"
      >
        <div
          className="
            w-full
            px-1
            pb-2
            pt-3
            text-left
            sm:px-3
          "
        >
          {/* =========================================
              MODAL ICON
          ========================================= */}

          <div
            className="
              mb-5
              flex
              h-12
              w-12
              items-center
              justify-center
              rounded-2xl
              bg-blue-50
              text-blue-600
            "
          >
            {forgotPasswordStep === 1 ? (
              <MailIcon />
            ) : (
              <LockIcon />
            )}
          </div>

          {/* =========================================
              MODAL HEADING
          ========================================= */}

          <div
            className="
              mb-6
              w-full
              text-left
            "
          >
            <h2
              className="
                text-left
                text-xl
                font-bold
                leading-7
                text-slate-900
              "
            >
              {forgotPasswordStep === 1
                ? "Forgot your password?"
                : "Create new password"}
            </h2>

            <p
              className="
                mt-2
                text-left
                text-sm
                leading-6
                text-slate-500
              "
            >
              {forgotPasswordStep === 1
                ? "Enter your registered email address and we'll send you a verification OTP."
                : "Enter the OTP sent to your email and choose your new password."}
            </p>
          </div>

          {/* =========================================
              FORGOT PASSWORD FORM
          ========================================= */}

          <Form
            form={forgotPasswordForm}
            layout="vertical"
            onFinish={
              forgotPasswordStep === 1
                ? handleSendOtp
                : handleResetPassword
            }
            requiredMark={false}
            className="w-full"
          >
            {forgotPasswordStep === 1 ? (
              <>
                {/* EMAIL */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      Email Address
                    </span>
                  }
                  name="emailId"
                  rules={[
                    {
                      required: true,
                      type: "email",
                      message:
                        "Please enter a valid email!",
                    },
                  ]}
                  className="!mb-5"
                >
                  <Input
                    size="large"
                    placeholder="example@mail.com"
                    prefix={
                      <span className="mr-2 text-slate-400">
                        <MailIcon />
                      </span>
                    }
                    className="
                      !h-[48px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !shadow-none
                      hover:!border-blue-400
                    "
                  />
                </Form.Item>

                {/* SEND OTP */}

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={modalLoading}
                  block
                  className="
                    !flex
                    !h-[48px]
                    !items-center
                    !justify-center
                    !rounded-xl
                    !border-none
                    !bg-blue-600
                    !text-sm
                    !font-semibold
                    !shadow-none
                    hover:!bg-blue-700
                  "
                >
                  Send OTP
                </Button>
              </>
            ) : (
              <>
                {/* =====================================
                    OTP SENT INFORMATION
                ===================================== */}

                <div
                  className="
                    mb-5
                    flex
                    w-full
                    items-start
                    gap-3
                    rounded-xl
                    border
                    border-blue-100
                    bg-blue-50/70
                    p-3.5
                    text-left
                  "
                >
                  <div
                    className="
                      mt-0.5
                      flex
                      h-7
                      w-7
                      shrink-0
                      items-center
                      justify-center
                      rounded-lg
                      bg-blue-100
                      text-xs
                      font-bold
                      text-blue-600
                    "
                  >
                    ✓
                  </div>

                  <div className="min-w-0">
                    <p
                      className="
                        text-xs
                        leading-5
                        text-slate-600
                      "
                    >
                      OTP sent successfully to
                    </p>

                    <p
                      className="
                        break-all
                        text-xs
                        font-semibold
                        leading-5
                        text-slate-800
                      "
                    >
                      {resetEmail}
                    </p>
                  </div>
                </div>

                {/* OTP */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      Verification OTP
                    </span>
                  }
                  name="otp"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter the OTP!",
                    },
                  ]}
                  className="!mb-5"
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Enter 6-digit OTP"
                    className="
                      !h-[48px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !tracking-[0.2em]
                      !shadow-none
                      hover:!border-blue-400
                    "
                  />
                </Form.Item>

                {/* NEW PASSWORD */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      New Password
                    </span>
                  }
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please enter your new password!",
                    },
                    {
                      min: 6,
                      message:
                        "Password must be at least 6 characters",
                    },
                  ]}
                  className="!mb-5"
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter new password"
                    prefix={
                      <span className="mr-2 text-slate-400">
                        <LockIcon />
                      </span>
                    }
                    className="
                      !h-[48px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !shadow-none
                      hover:!border-blue-400
                    "
                  />
                </Form.Item>

                {/* CONFIRM PASSWORD */}

                <Form.Item
                  label={
                    <span
                      className="
                        block
                        text-left
                        text-[13px]
                        font-semibold
                        text-slate-700
                      "
                    >
                      Confirm Password
                    </span>
                  }
                  name="confirmPassword"
                  dependencies={[
                    "newPassword",
                  ]}
                  rules={[
                    {
                      required: true,
                      message:
                        "Please confirm your password!",
                    },

                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue(
                            "newPassword"
                          ) === value
                        ) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error(
                            "The two passwords do not match!"
                          )
                        );
                      },
                    }),
                  ]}
                  className="!mb-5"
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm new password"
                    prefix={
                      <span className="mr-2 text-slate-400">
                        <LockIcon />
                      </span>
                    }
                    className="
                      !h-[48px]
                      !w-full
                      !rounded-xl
                      !border-slate-200
                      !px-4
                      !text-sm
                      !shadow-none
                      hover:!border-blue-400
                    "
                  />
                </Form.Item>

                {/* RESET PASSWORD */}

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={modalLoading}
                  block
                  className="
                    !flex
                    !h-[48px]
                    !items-center
                    !justify-center
                    !rounded-xl
                    !border-none
                    !bg-blue-600
                    !text-sm
                    !font-semibold
                    !shadow-none
                    hover:!bg-blue-700
                  "
                >
                  Reset Password
                </Button>

                {/* BACK */}

                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordStep(1);

                    forgotPasswordForm.resetFields();
                  }}
                  className="
                    mt-4
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    text-sm
                    font-semibold
                    text-slate-500
                    transition-colors
                    hover:text-blue-600
                  "
                >
                  <ArrowLeftIcon />

                  Back to email
                </button>
              </>
            )}
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default LoginForm;
